import {axiosInstance, setAxiosBaseInstance, setAxiosInstance} from '~/utils/axios-accessor'
import {authStore} from '@/composables/store-accessor'
import api from '@/api'
import {stringify} from 'qs'
import {EventBus, LOGOUT_REDIRECT_EVENT} from '@/utils/event-bus'
import {formatAxiosError} from '@/utils/helpers'
import {srcHost} from '~src/utils/host-adapter'

// From @nuxtjs/axios below
import Axios, {type AxiosRequestConfig} from 'axios'
import defu from 'defu'

// Axios.prototype cannot be modified
const axiosExtra: any = {
    setBaseURL(baseURL: string) {
        this.defaults.baseURL = baseURL
    },
    setHeader(name: string, value: string | null, scopes = 'common') {
        for (const scope of Array.isArray(scopes) ? scopes : [scopes]) {
            if (!value) {
                // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                delete this.defaults.headers[scope][name]
                continue
            }
            this.defaults.headers[scope][name] = value
        }
    },
    setToken(token: string, type: string, scopes = 'common') {
        const value = !token ? null : (type ? type + ' ' : '') + token
        this.setHeader('Authorization', value, scopes)
    },
    onRequest(fn: (arg0: any) => any) {
        this.interceptors.request.use((config: any) => fn(config) || config)
    },
    onResponse(fn: (arg0: any) => any) {
        this.interceptors.response.use((response: any) => fn(response) || response)
    },
    onRequestError(fn: (arg0: any) => any) {
        this.interceptors.request.use(undefined, (error: any) => fn(error) || Promise.reject(error))
    },
    onResponseError(fn: (arg0: any) => any) {
        this.interceptors.response.use(undefined, (error: any) => fn(error) || Promise.reject(error))
    },
    onError(fn: {(arg0: any): any; (arg0: any): any}) {
        this.onRequestError(fn)
        this.onResponseError(fn)
    },
    create(options: any) {
        return createAxiosInstance(defu(options, this.defaults))
    },
}

// Request helpers ($get, $post, ...)
for (const method of ['request', 'delete', 'get', 'head', 'options', 'post', 'put', 'patch']) {
    axiosExtra['$' + method] = function () {
        // eslint-disable-next-line prefer-spread,prefer-rest-params
        return this[method].apply(this, arguments).then((res: {data: any}) => res && res.data)
    }
}

const extendAxiosInstance = (axios: any) => {
    for (const key in axiosExtra) {
        axios[key] = axiosExtra[key].bind(axios)
    }
}

const createAxiosInstance = (axiosOptions: AxiosRequestConfig | undefined) => {
    // Create new axios instance
    const axios: any = Axios.create(axiosOptions)
    axios.CancelToken = Axios.CancelToken
    axios.isCancel = Axios.isCancel

    // Extend axios proto
    extendAxiosInstance(axios)

    // Intercept to apply default headers
    axios.onRequest((config: {headers: any}) => {
        config.headers = {...axios.defaults.headers.common, ...config.headers}
    })

    return axios
}

// From @nuxtjs/axios above

/**
 * Format date to yyyy-MM-dd
 * @param date
 */
const formatDate = date =>
    `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`

const dateTransformer = data => {
    if (data instanceof FormData) {
        return data
    }
    if (data instanceof Date) {
        return formatDate(data)
    }
    if (Array.isArray(data)) {
        return data.map(val => dateTransformer(val))
    }
    if (typeof data === 'object' && data !== null) {
        return Object.fromEntries(Object.entries(data).map(([key, val]) => [key, dateTransformer(val)]))
    }
    return data
}

export default defineNuxtPlugin(() => {
    const {$i18n, $conf} = useNuxtApp()
    const {origin} = useRequestURL()
    const paramsSerializer = params => stringify(dateTransformer(params), {arrayFormat: 'repeat'})
    const axios = createAxiosInstance({
        baseURL: origin,
        paramsSerializer,
        transformRequest: [dateTransformer, ...Axios.defaults.transformRequest],
    })
    setAxiosBaseInstance(
        axios.create({
            headers: {common: {}},
            paramsSerializer,
        })
    )
    if (useCookie('token').value) {
        axios.setHeader('Token', useCookie('token').value)
    }
    setAxiosInstance(axios)
    axios.onError(
        async (error: {
            response: {status: number; data: any}
            config: {url: string | string[]; headers: {Token: any}}
        }) => {
            if (
                error.response &&
                error.response.status === 401 &&
                (![srcHost('/proxy/authorization'), srcHost('/api/authorization')].includes(error.config.url) ||
                    error.config.url.includes('authorizationInfo'))
            ) {
                if (authStore.person?.role) {
                    EventBus.$emit(LOGOUT_REDIRECT_EVENT)
                    authStore.logOut()
                    navigateTo('/')
                    return error
                }
                error.config.headers.Token = await authStore.auth()
                return await axios.request(error.config)
            }
            if (axiosInstance.isCancel(error)) {
                // TODO [P4] Right way - Promise.reject - need review all cancel cases
                return error
            }
            if (error.config && (error.config.url.indexOf('/proxy/', 0) === -1 || !error.response)) {
                if (error.config.url.indexOf('/cms/') === 0 && $conf.cms.required) {
                    console.error(error.config.url, error.response.status)
                    showError({
                        statusCode: 503,
                        message: $i18n.t('error_message.service_unavailable'),
                    })
                }

                // TODO [P3] Need setup
                // ctx.$sentry.captureException(error)
                const errorData = formatAxiosError(error)
                delete errorData.request
                return Promise.reject(errorData)
            } else if (error.response) {
                if (error.response.status !== 401) {
                    // TODO [P3] Need setup
                    // ctx.$sentry.captureException(error)
                }
                return Promise.reject(
                    typeof error.response.data !== 'string'
                        ? error.response.data
                        : {msg: $i18n.t('error_message.network_error')}
                )
            } else {
                console.error(error)
                return Promise.reject(error)
            }
        }
    )
    return {provide: {api, axios}}
})
